"use client"

import DashboardContentWrapper from "../../../../../../shared/components/dashboard-content-wrapper"
import Heading from "../../../../../../shared/components/ui/typography/heading"
import { DASHBOARD_ROUTE } from "../../../../../../shared/constants/pages-routes"
import { createWorkOrder, getWorkOrderById } from "@/features/work-orders/actions"
import { formatWorkScope } from "@/features/work-orders/utils/format-work-scope"
import WorkOrderForm from "@/features/work-orders/components/forms/work-order-form"
import { Button } from "@/shared/components/ui/button"
import { ChevronLeft, Copy } from "lucide-react"
import Link from "next/link"
import React, { useEffect, useState } from "react"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { useParams, useRouter } from "next/navigation"
import { useRegionId } from "@/shared/hooks/use-region-id"
import successToast from "@/shared/lib/toast/success.toast"
import errorToast from "@/shared/lib/toast/error.toast"
import ScrollToTopFab from "@/shared/components/scroll-to-top-fab"
import PrintButton from "@/features/work-orders/components/print-button"

interface WorkOrderEditPageProps {}

function WorkOrderEditPage() {
    const { session, profile, isLoading: sessionLoading } = useSession()
    const router = useRouter()
    const urlParams = useParams()
    const regionSlug = (urlParams?.region as string) || ""
    const { regionId } = useRegionId()
    const [workOrderData, setWorkOrderData] = React.useState<any>(null)
    const [isLoading, setIsLoading] = React.useState(true)
    const [isDuplicating, setIsDuplicating] = useState(false)

    const handleDuplicate = async () => {
        if (!workOrderData?.home?.id || isDuplicating) return
        setIsDuplicating(true)
        try {
            const toolsNeeded = Array.isArray(workOrderData.toolsNeeded)
                ? workOrderData.toolsNeeded.map((item: any) =>
                      typeof item === "object" && item != null && "name" in item
                          ? { name: item.name ?? item, out: item.out ?? false, in: item.in ?? false }
                          : { name: String(item ?? ""), out: false, in: false }
                  )
                : []
            const suppliesNeeded = Array.isArray(workOrderData.suppliesNeeded)
                ? workOrderData.suppliesNeeded.map((item: any) =>
                      typeof item === "object" && item != null && "name" in item
                          ? { name: item.name ?? item, out: item.out ?? false, in: item.in ?? false }
                          : { name: String(item ?? ""), out: false, in: false }
                  )
                : []

            const duplicatePayload = {
                workOrderType: workOrderData.workOrderType ?? "",
                workScopeId: workOrderData.workScopeId ?? workOrderData.workScope?.id ?? null,
                homeId: workOrderData.home.id,
                ownerId: workOrderData.owner?.id ?? 0,
                teamName: "",
                teamLeadId: 0,
                volunteersNeeded: workOrderData.volunteersNeeded,
                projectedVolunteerHours: workOrderData.projectedVolunteerHours,
                projectDescription: workOrderData.projectDescription ?? "",
                notes: workOrderData.notes ?? "",
                volunteerIds: [],
                toolsNeeded,
                suppliesNeeded,
                workOrderHours: [],
            }

            const response = await createWorkOrder(duplicatePayload as any)
            if (response.success && response.data) {
                const newId = (response.data as any)?.id
                if (newId != null) {
                    successToast("Work order duplicated. Edit the new copy to assign team and work days.")
                    router.push(`/${regionSlug}/work-orders/edit/${newId}`)
                    return
                }
            }
            errorToast((response as any)?.error ?? "Failed to duplicate work order.")
        } catch (e) {
            errorToast(e instanceof Error ? e.message : "Failed to duplicate work order.")
        } finally {
            setIsDuplicating(false)
        }
    }

    useEffect(() => {
        const checkAccess = async () => {
            // Wait for session to load
            if (sessionLoading || regionId == null) return

            // Get work order data first
            const id = urlParams?.id
            if (!id) return
            const workOrderId = parseInt(id)

            try {
                const workOrderResponse = await getWorkOrderById(workOrderId)

                if (!workOrderResponse.success || !workOrderResponse.data) {
                    router.push(DASHBOARD_ROUTE.workOrders)
                    return
                }

                const workOrder = workOrderResponse.data
                const workOrderRegionId =
                    (workOrder as any)?.home?.regionId ??
                    (workOrder as any)?.home?.region?.id ??
                    null
                if (
                    workOrderRegionId != null &&
                    Number(workOrderRegionId) !== Number(regionId)
                ) {
                    errorToast("This work order is not in that region.")
                    router.replace(`/${regionSlug}${DASHBOARD_ROUTE.workOrders}`)
                    return
                }

                // Check if user has permission to edit
                // Only Admin and BaseLeader can edit work orders
                const isAdmin = session?.role === UserRole.ADMIN
                const isBaseLeader = session?.role === UserRole.BASE_LEADER

                const canEditWorkOrder = isAdmin || isBaseLeader

                if (!canEditWorkOrder) {
                    router.push(DASHBOARD_ROUTE.workOrders)
                    return
                }

                setWorkOrderData(workOrder)
            } catch (error) {
                console.error("Error in WorkOrderEditPage:", error)
                router.push(DASHBOARD_ROUTE.workOrders)
            }

            setIsLoading(false)
        }

        checkAccess()
    }, [session, profile, sessionLoading, router, urlParams?.id, regionId, regionSlug])

    // Show loading state
    if (isLoading || sessionLoading) {
        return (
            <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                        <p className="text-muted-foreground">Loading...</p>
                    </div>
                </div>
            </DashboardContentWrapper>
        )
    }

    return (
        <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
            <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-center gap-0.5">
                    <button onClick={() => router.back()} className="cursor-pointer">
                        <ChevronLeft className="w-7 h-7" />
                    </button>
                    <Heading>
                        Edit {workOrderData?.workOrderType ? `${formatWorkScope(workOrderData.workOrderType)} ` : ""}Work Order
                    </Heading>
                </div>
                <div className="flex items-center gap-2">
                    <PrintButton title="Print Work Order" />
                    <Button
                        variant="default"
                        size="icon"
                        className="print:hidden md:w-auto md:px-4 md:gap-2"
                        onClick={handleDuplicate}
                        disabled={isDuplicating}
                        title="Duplicate Work Order"
                        showLoader={isDuplicating}
                    >
                        <Copy className="w-4 h-4 shrink-0" />
                        <span className="hidden md:inline">Duplicate</span>
                    </Button>
                </div>
            </div>
            <WorkOrderForm 
                mode="edit" 
                workOrder={workOrderData} 
                workOrderType={workOrderData?.workOrderType}
            />
            <ScrollToTopFab />
        </DashboardContentWrapper>
    )
}

export default WorkOrderEditPage
